import {
  Component,
  ViewChild,
  AfterViewInit,
  TemplateRef,
  Inject,
  inject,
  Renderer2,
  DOCUMENT
} from '@angular/core';
import { FieldTypeConfig, FormlyFieldConfig, ɵdefineHiddenProp as defineHiddenProp, FormlyModule } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material/form-field';
import { MatInput } from '@angular/material/input';
import { MAT_DATETIME_FORMATS, MatDatetimepickerComponent, MatDatetimepickerInputDirective, MatDatetimepickerModule } from '@mat-datetimepicker/core';
import { MatMomentDatetimeModule } from '@mat-datetimepicker/moment';
import {
  futureDateTimeValidation,
  futureDateValidation,
  pastDateTimeValidation,
  pastDateValidation,
} from '../../validators';
import { get, isEmpty } from 'lodash';

import { asyncScheduler } from 'rxjs';
import { ReactiveFormsModule } from '@angular/forms';

@Component({
    selector: 'app-formly-field-datetime',
    template: `
    <input
      matInput
      [id]="id"
      [formControl]="formControl"
      [errorStateMatcher]="errorStateMatcher"
      [matDatetimepicker]="datetimePicker"
      [max]="to.datetimepickerOptions.max"
      [min]="to.datetimepickerOptions.min"
      [formlyAttributes]="field"
      [placeholder]="to.placeholder"
      [tabindex]="to.tabindex"
      [readonly]="true"
      [required]="to.required"
      (click)="openDatePicker()"
    />
    <ng-template #matSuffix>
      <mat-datetimepicker-toggle #datetimepickerToggle
        [for]="datetimePicker"
      ></mat-datetimepicker-toggle>
    </ng-template>
    
    <mat-datetimepicker
      #datetimePicker
      [type]="to.type"
      [disabled]="to.disabled || to.readonly"
      [openOnFocus]="to.datetimepickerOptions.openOnFocus"
      [timeInterval]="to.datetimepickerOptions.timeInterval"
      (opened)="onOpenPicker()"
    >
    </mat-datetimepicker>
  `,
    providers: [
        {
            provide: MAT_DATETIME_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'DD MMM YYYY',
                    monthInput: 'MMMM',
                    timeInput: 'LT',
                    datetimeInput: 'DD MMM YYYY LT',
                },
                display: {
                    dateInput: 'DD MMM YYYY',
                    monthInput: 'MMMM',
                    datetimeInput: 'DD MMM YYYY LT',
                    timeInput: 'LT',
                    monthYearLabel: 'MMM YYYY',
                    dateA11yLabel: 'LL',
                    monthYearA11yLabel: 'MMMM YYYY',
                    popupHeaderDateLabel: 'ddd, DD MMM',
                },
            },
        },
    ],
    imports: [MatInput, ReactiveFormsModule, MatDatetimepickerModule, MatMomentDatetimeModule, FormlyModule]
})
export class FormlyFieldDateTimeComponent
  extends FieldType<FormlyFieldConfig>
  implements AfterViewInit
{
  @ViewChild(MatInput, { static: true } as any) formFieldControl!: MatInput;
  @ViewChild(MatDatetimepickerInputDirective)
  datetimepickerInput!: MatDatetimepickerInputDirective<any>;
  @ViewChild('datetimepickerToggle') datetimepickerToggle!: TemplateRef<any>;
  @ViewChild('datetimePicker') datetimepicker!: MatDatetimepickerComponent<any>;
  defaultOptions: FormlyFieldConfig = {
    templateOptions: {
      datetimepickerOptions: {
        type: 'datetime',
        startView: 'month',
        openOnFocus: false,
        timeInterval: 1,
        datetimepickerTogglePosition: 'suffix',
        dateInput: () => {},
        dateChange: () => {},
      },
    },
  };

  private document = inject(DOCUMENT);
  private renderer = inject(Renderer2);

  constructor() {
    super();
  }

  openDatePicker(): void {  
    this.datetimepicker.open();
  }

  ngAfterViewInit(): void {
    (this.datetimepickerInput as any)._formField = this.formField;

    // add validators only if dateValidation has a value in it
    if (this.to.dateValidation) {
      const validationCheck =
        this.to.dateValidation === 1
          ? get(this.field, 'templateOptions.type') === 'date'
            ? futureDateValidation
            : futureDateTimeValidation
          : get(this.field, 'templateOptions.type') === 'date'
          ? pastDateValidation
          : pastDateTimeValidation;
      this.formControl.addValidators([validationCheck]);
    }
    setTimeout(() => {
      defineHiddenProp(
        this.field,
        '_mat' + this.to.datetimepickerOptions.datetimepickerTogglePosition,
        this.datetimepickerToggle
      );
      (this.options as any).detectChanges(this.field);
    }
  );
  }

  onOpenPicker(): void {
    asyncScheduler.schedule(() => {
      const calendarEL = this.document.querySelector(".mat-datetimepicker-calendar") as HTMLElement;
      const yearButton = this.document.querySelector(".mat-datetimepicker-calendar-header-year") as HTMLElement;
      const monthButton = this.document.querySelector(".mat-datetimepicker-calendar-header-date") as HTMLElement;
      const previousButton = this.document.querySelector(".mat-datetimepicker-calendar-previous-button") as HTMLElement;
      const nextButton = this.document.querySelector(".mat-datetimepicker-calendar-next-button") as HTMLElement;
      const clockView = this.document.querySelector(".mat-datetimepicker-clock") as HTMLElement;

      this.removeAccessibility(calendarEL);
      this.setAccessability(yearButton);
      this.setAccessability(monthButton);
      this.setAccessability(previousButton);
      this.addFocusStyles(previousButton, { borderRadius: '50%' });
      this.setAccessability(nextButton);
      this.addFocusStyles(nextButton, { borderRadius: '50%' });
      this.setAccessability(clockView);
      
      if (calendarEL) {
        calendarEL.focus();
      }
    });
  }

  setAccessability(el: HTMLElement): void {
    if (!el) {
      return;
    }
    this.renderer.removeAttribute(el, "tabindex");
    this.renderer.listen(el, "keydown", (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === " ") {
        event.preventDefault();
        event.stopPropagation();
        asyncScheduler.schedule(() => {
          el.click();
        });
      }
    });
  }

  removeAccessibility(el: HTMLElement): void {
    if (!el) {
      return;
    }
    this.renderer.setAttribute(el, "tabindex", "0");
  }

  addFocusStyles(el: HTMLElement, extraStyles?: Partial<CSSStyleDeclaration>): void {
    this.renderer.addClass(el, "focus-style");
    if (!isEmpty(extraStyles)) {
      for (const style in extraStyles) {
        this.renderer.setStyle(el, style, extraStyles[style]);
      }
    }
  }

}
